'use client'

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Command } from 'cmdk'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { tenderApi } from '@/lib/api-client'
import {
  getNavItemByHref,
  isAdminRoles,
  visibleMorePages,
  visibleNavSections,
  type NavItem,
} from '@/lib/nav-items'

interface CommandPaletteProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const OPEN_EVENT = 'pa:open-command-palette'

/** Opens the palette from anywhere (the header search field, the mobile search icon). */
export function openCommandPalette() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(OPEN_EVENT))
}

// The last three pages visited this session. Memory only; it resets on reload.
const RECENT_LIMIT = 3
let recentHrefs: string[] = []

function rememberVisit(href: string) {
  if (!getNavItemByHref(href)) return
  recentHrefs = [href, ...recentHrefs.filter((h) => h !== href)].slice(0, RECENT_LIMIT)
}

interface TenderHit {
  ref: string
  title: string
}

/** A tender reference such as TND-NHS-004 or PSR-NHS-002. */
const TENDER_REF = /^[a-z]{2,5}-[a-z]{2,5}-\d{3}$/i

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState<T>(value)
  React.useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debounced
}

const ITEM_CLASS = cn(
  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5',
  'text-sm text-foreground',
  'aria-selected:bg-brand-navy aria-selected:text-white',
  'transition-colors'
)

const GROUP_CLASS =
  'px-2 py-1.5 text-xs font-semibold text-muted-foreground [&_[cmdk-group-heading]]:px-1 [&_[cmdk-group-heading]]:pb-1'

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [showAbout, setShowAbout] = React.useState(false)
  const [tenders, setTenders] = React.useState<TenderHit[]>([])
  const debouncedSearch = useDebounce(search, 150)
  const router = useRouter()
  const pathname = usePathname()
  const { data: session } = useSession()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const controlledOpen = open !== undefined ? open : isOpen
  const setControlledOpen = onOpenChange || setIsOpen

  const isAdmin = isAdminRoles(session?.roles)
  const navItems = React.useMemo(
    () => visibleNavSections(isAdmin).flatMap((section) => section.items),
    [isAdmin]
  )
  const morePages = React.useMemo(() => visibleMorePages(isAdmin), [isAdmin])
  const startProcess = navItems.find((item) => item.href === '/admin/start-process')
  // The requisitioner page is off the rail; it shows up with the Supply Chain pages.
  const hasSupplyChain = navItems.some((item) => item.href === '/supply-chain')
  const aboutItem = getNavItemByHref(pathname)

  React.useEffect(() => {
    rememberVisit(pathname)
  }, [pathname])

  // Cmd+K or Ctrl+K toggles; the header search field dispatches OPEN_EVENT.
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setControlledOpen(!controlledOpen)
      }
    }
    const openFromEvent = () => setControlledOpen(true)
    document.addEventListener('keydown', down)
    window.addEventListener(OPEN_EVENT, openFromEvent)
    return () => {
      document.removeEventListener('keydown', down)
      window.removeEventListener(OPEN_EVENT, openFromEvent)
    }
  }, [controlledOpen, setControlledOpen])

  // Focus the input when opened; clear the search and load tenders for search.
  React.useEffect(() => {
    if (!controlledOpen) {
      setSearch('')
      setShowAbout(false)
      return
    }
    setTimeout(() => inputRef.current?.focus(), 0)
    let cancelled = false
    tenderApi
      .getPipeline()
      .then((response) => {
        if (cancelled) return
        setTenders(
          (response.items ?? []).map((item) => ({
            ref: item.tender_ref,
            title: item.title,
          }))
        )
      })
      .catch(() => {
        // Search still opens a reference typed in full.
        if (!cancelled) setTenders([])
      })
    return () => {
      cancelled = true
    }
  }, [controlledOpen])

  const go = (href: string) => {
    setControlledOpen(false)
    setSearch('')
    router.push(href)
  }

  const query = debouncedSearch.trim()
  const hits = query
    ? tenders
        .filter(
          (t) =>
            t.ref.toLowerCase().includes(query.toLowerCase()) ||
            t.title.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 6)
    : []
  const typedRef = TENDER_REF.test(query) && !hits.some((h) => h.ref.toLowerCase() === query.toLowerCase())
    ? query.toUpperCase()
    : null

  const recent = recentHrefs
    .filter((href) => href !== pathname)
    .map((href) => getNavItemByHref(href))
    .filter((item): item is NavItem => Boolean(item))

  const item = (id: string, label: string, Icon: React.ElementType, onSelect: () => void, hint?: string) => (
    <Command.Item
      key={id}
      value={`${label} ${hint ?? ''}`}
      onSelect={onSelect}
      className={ITEM_CLASS}
    >
      <Icon className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
      <span>{label}</span>
    </Command.Item>
  )

  return (
    <Dialog open={controlledOpen} onOpenChange={setControlledOpen}>
      <DialogContent className='overflow-hidden p-0 sm:max-w-xl [&>button]:hidden'>
        <DialogTitle className='sr-only'>Search and commands</DialogTitle>
        <Command className={cn('overflow-hidden rounded-xl bg-white/95 backdrop-blur-xl')} loop>
          <div className='flex items-center border-b border-border/50 px-4'>
            <Icons.search className='h-4 w-4 shrink-0 text-muted-foreground' aria-hidden='true' />
            <Command.Input
              ref={inputRef}
              value={search}
              onValueChange={(value) => {
                setSearch(value)
                setShowAbout(false)
              }}
              placeholder='Type a command or search...'
              className={cn(
                'h-14 flex-1 bg-transparent px-3 text-foreground',
                'placeholder:text-muted-foreground focus:outline-none'
              )}
            />
            <kbd className='hidden h-6 items-center rounded border border-border/50 bg-muted/50 px-2 text-xs text-muted-foreground sm:inline-flex'>
              ESC
            </kbd>
          </div>

          {showAbout && aboutItem ? (
            <div className='max-h-[300px] overflow-y-auto p-4'>
              <p className='text-sm font-semibold text-brand-navy'>{aboutItem.label}</p>
              <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                {aboutItem.description}
              </p>
            </div>
          ) : (
            <Command.List className='max-h-[300px] overflow-y-auto p-2' aria-live='polite'>
              <Command.Empty className='py-6 text-center text-sm text-muted-foreground'>
                Nothing matches. Try a tender reference such as TND-NHS-004.
              </Command.Empty>

              {(hits.length > 0 || typedRef) && (
                <Command.Group heading='Procurements' className={GROUP_CLASS}>
                  {hits.map((hit) =>
                    item(`tender-${hit.ref}`, `${hit.ref}  ${hit.title}`, Icons.clipboardList, () => go(`/tender/${hit.ref}`), hit.ref)
                  )}
                  {typedRef &&
                    item(`ref-${typedRef}`, `Open ${typedRef}`, Icons.clipboardList, () => go(`/tender/${typedRef}`), typedRef)}
                </Command.Group>
              )}

              {!debouncedSearch && recent.length > 0 && (
                <Command.Group heading='Recent' className={GROUP_CLASS}>
                  {recent.map((r) => item(`recent-${r.href}`, r.label, r.icon, () => go(r.href), 'recent'))}
                </Command.Group>
              )}

              <Command.Group heading='Navigation' className={GROUP_CLASS}>
                {navItems.map((n) =>
                  item(`nav-${n.href}`, `Go to ${n.label}`, n.icon, () => go(n.href), n.description)
                )}
              </Command.Group>

              {morePages.length > 0 && (
                <Command.Group heading='More pages' className={GROUP_CLASS}>
                  {morePages.map((n) =>
                    item(`more-${n.href}`, n.label, n.icon, () => go(n.href), n.description)
                  )}
                </Command.Group>
              )}

              <Command.Group heading='Actions' className={GROUP_CLASS}>
                {startProcess &&
                  item('start-process', 'Start new process', Icons.plus, () => go(startProcess.href), 'new procurement')}
                {hasSupplyChain &&
                  item(
                    'ask-sophie',
                    'Ask Sophie about buying or deliveries',
                    Icons.messageSquare,
                    () => go('/supply-chain/ask'),
                    'the requisitioner page'
                  )}
                {item('refresh', 'Refresh Data', Icons.refresh, () => window.location.reload())}
              </Command.Group>

              <Command.Group heading='Settings' className={GROUP_CLASS}>
                {item('settings', 'Open Settings', Icons.settings, () => go('/settings'))}
                {aboutItem &&
                  item('about', 'About this page', Icons.info, () => setShowAbout(true), aboutItem.label)}
              </Command.Group>
            </Command.List>
          )}

          <div className='flex items-center justify-between border-t border-border/50 px-4 py-2 text-xs text-muted-foreground'>
            <div className='flex items-center gap-4'>
              <span className='flex items-center gap-1'>
                <kbd className='rounded border border-border/50 bg-muted/50 px-1'>↑↓</kbd>
                Navigate
              </span>
              <span className='flex items-center gap-1'>
                <kbd className='rounded border border-border/50 bg-muted/50 px-1'>↵</kbd>
                Select
              </span>
            </div>
            <span className='flex items-center gap-1'>
              <kbd className='rounded border border-border/50 bg-muted/50 px-1'>ESC</kbd>
              Close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
